import { useState } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  Space,
  Divider,
  Typography,
  List,
  message,
  Row,
  Col
} from 'antd';
import { LockOutlined, UserOutlined, DatabaseOutlined, SettingOutlined } from '@ant-design/icons';
import AdminLayout from '../../components/AdminLayout';
import { supabase } from '../../lib/supabase';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

export default function SettingsPage() {
  const [userLoading, setUserLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleUserProfileUpdate = async (values: any) => {
    try {
      setUserLoading(true);
      
      // This is a placeholder for the actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success('Profiel bijgewerkt!');
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Kon profiel niet bijwerken');
    } finally {
      setUserLoading(false);
    }
  };

  const handlePasswordUpdate = async (values: any) => {
    try {
      setPasswordLoading(true);
      
      if (values.newPassword !== values.confirmPassword) {
        throw new Error('Wachtwoorden komen niet overeen');
      }
      
      // This is a placeholder for the actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success('Wachtwoord bijgewerkt!');
    } catch (error: any) {
      console.error('Error updating password:', error);
      message.error(error.message || 'Kon wachtwoord niet bijwerken');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ borderRadius: '8px' }}>
        <Title level={4}>Instellingen</Title>
        <Tabs defaultActiveKey="profile">
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                Gebruikersprofiel
              </span>
            } 
            key="profile"
          >
            <Card style={{ marginTop: 20 }}>
              <Form
                layout="vertical"
                onFinish={handleUserProfileUpdate}
                initialValues={{
                  email: 'admin@repacked.nl',
                  displayName: 'Admin Gebruiker',
                }}
              >
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="E-mailadres"
                      rules={[
                        { required: true, message: 'E-mailadres is verplicht!' },
                        { type: 'email', message: 'Ongeldig e-mailadres!' }
                      ]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="E-mailadres" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="displayName"
                      label="Weergavenaam"
                      rules={[{ required: true, message: 'Naam is verplicht!' }]}
                    >
                      <Input placeholder="Weergavenaam" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={userLoading}>
                    Profiel Bijwerken
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <LockOutlined />
                Beveiliging
              </span>
            } 
            key="security"
          >
            <Card style={{ marginTop: 20 }}>
              <Form
                layout="vertical"
                onFinish={handlePasswordUpdate}
              >
                <Row gutter={24}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="currentPassword"
                      label="Huidig Wachtwoord"
                      rules={[{ required: true, message: 'Voer uw huidige wachtwoord in!' }]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder="Huidig Wachtwoord" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="newPassword"
                      label="Nieuw Wachtwoord"
                      rules={[
                        { required: true, message: 'Voer uw nieuwe wachtwoord in!' },
                        { min: 8, message: 'Wachtwoord moet minstens 8 tekens bevatten!' }
                      ]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder="Nieuw Wachtwoord" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="confirmPassword"
                      label="Bevestig Wachtwoord"
                      rules={[
                        { required: true, message: 'Bevestig uw wachtwoord!' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('newPassword') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('De wachtwoorden komen niet overeen!'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder="Bevestig Wachtwoord" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={passwordLoading}>
                    Wachtwoord Bijwerken
                  </Button>
                </Form.Item>
                
                <Divider />
                
                <Title level={5}>Wachtwoordvereisten</Title>
                <Paragraph>
                  Wachtwoorden moeten:
                </Paragraph>
                <ul>
                  <li>Minstens 8 tekens lang zijn</li>
                  <li>Ten minste één hoofdletter bevatten</li>
                  <li>Ten minste één kleine letter bevatten</li>
                  <li>Ten minste één cijfer bevatten</li>
                </ul>
              </Form>
            </Card>
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <DatabaseOutlined />
                Database Informatie
              </span>
            } 
            key="database"
          >
            <Card style={{ marginTop: 20 }}>
              <List
                bordered
                itemLayout="horizontal"
              >
                <List.Item>
                  <List.Item.Meta
                    title="Database Versie"
                    description="Supabase v1.0.0"
                  />
                </List.Item>
                <List.Item>
                  <List.Item.Meta
                    title="Database URL"
                    description="https://ivtllicbozkouaeurjjc.supabase.co"
                  />
                </List.Item>
                <List.Item>
                  <List.Item.Meta
                    title="Tabellen"
                    description="producten, leveranciers, gebruikers, toegang, bestellingen"
                  />
                </List.Item>
              </List>
              
              <Divider />
              
              <Space direction="vertical">
                <Text strong>Let op:</Text>
                <Text>Databaseconfiguratie kan alleen worden aangepast via de Supabase beheerderspaneel.</Text>
                <Button type="primary" href="https://app.supabase.io" target="_blank">
                  Ga naar Supabase Dashboard
                </Button>
              </Space>
            </Card>
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                Algemene Instellingen
              </span>
            } 
            key="general"
          >
            <Card style={{ marginTop: 20 }}>
              <Form layout="vertical">
                <Form.Item label="Donkere Modus" name="darkMode" valuePropName="checked">
                  <Switch defaultChecked={false} />
                </Form.Item>
                
                <Form.Item label="E-mailnotificaties" name="emailNotifications" valuePropName="checked">
                  <Switch defaultChecked={true} />
                </Form.Item>
                
                <Form.Item label="Dashboard Automatisch Vernieuwen" name="autoRefresh" valuePropName="checked">
                  <Switch defaultChecked={true} />
                </Form.Item>
                
                <Form.Item>
                  <Button type="primary">
                    Instellingen Opslaan
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
} 